import React, { useMemo } from 'react'
import { Autocomplete, TextField, Typography, Box } from '@mui/material'
import {
  asDate,
  getDeterminationDisplay,
  usePriorAuthorizations,
  type PriorAuthorizationListItem,
} from '~/features/member-profile/care-navigation/prior-auth'

// Custom hook to get prior authorizations with originator system IDs
const usePriorAuthorizationsWithOriginatorIds = (personId: number | null) => {
  const { data: priorAuthorizations = [], isPending: isLoading } = usePriorAuthorizations(personId)

  const priorAuthsWithOriginatorIds = useMemo(() => {
    return priorAuthorizations
      .filter(auth => {
        // Filter out any prior auths that are already associated with a steerage
        if (auth.steerage_case_ids.length) return false
        // Filter out any prior auths that don't have an originator system ID
        return auth.originator_system_id && auth.originator_system_id.trim() !== ''
      })
      .sort((a, b) => {
        // Sort by originator system ID
        const idA = a.originator_system_id || ''
        const idB = b.originator_system_id || ''
        return idA.localeCompare(idB)
      })
  }, [priorAuthorizations])

  return { priorAuthorizations: priorAuthsWithOriginatorIds, isLoading }
}

interface PriorAuthorizationSelectProps {
  value: string
  onChange: (value: string) => void
  // Fires when the user explicitly commits a value via the dropdown / X clear /
  // Enter on the free-solo input. Typing keystrokes do NOT fire this. Optional so
  // existing consumers that only need raw input updates remain unaffected.
  onSelectOption?: (value: string) => void
  error?: boolean
  helperText?: string
  disabled?: boolean
  personId: number | null
}

const PriorAuthorizationSelect: React.FC<PriorAuthorizationSelectProps> = ({
  value,
  onChange,
  onSelectOption,
  error,
  helperText,
  disabled,
  personId,
}) => {
  const { priorAuthorizations } = usePriorAuthorizationsWithOriginatorIds(personId)

  const handleAutocompleteChange = (
    _: React.SyntheticEvent,
    value: string | PriorAuthorizationListItem | null,
  ) => {
    const commit = (next: string) => {
      onChange(next)
      onSelectOption?.(next)
    }
    if (typeof value === 'string') return commit(value)
    if (value === null) return commit('')
    // PriorAuthorization object selected - use the originator system ID
    return commit(value.originator_system_id || '')
  }

  const handleInputChange = (_: React.SyntheticEvent, value: string) => {
    // Update the parent with the typed value for freeSolo behavior
    onChange(value)
  }

  // Find the selected option object
  const selectedOption = value
    ? (priorAuthorizations.find(auth => auth.originator_system_id === value) ?? null)
    : null

  return (
    <Autocomplete
      value={selectedOption}
      onChange={handleAutocompleteChange}
      inputValue={value}
      onInputChange={handleInputChange}
      options={priorAuthorizations}
      freeSolo
      disabled={disabled}
      getOptionLabel={option => {
        if (typeof option === 'string') return option
        return option?.originator_system_id || ''
      }}
      isOptionEqualToValue={(option, value) =>
        typeof option === 'string' || typeof value === 'string'
          ? option === value
          : option?.originator_system_id === value?.originator_system_id
      }
      renderOption={({ key, ...props }, option) => (
        <Box component="li" key={key} {...props} sx={{ py: 1 }}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {option.originator_system_id || ''}
            </Typography>
            <Typography
              variant="caption"
              component="p"
              sx={{ color: 'text.secondary', lineHeight: '1.25' }}
            >
              {getDeterminationDisplay(option)}
            </Typography>
            <Typography
              variant="caption"
              component="p"
              sx={{ color: 'text.secondary', lineHeight: '1.25' }}
            >
              {asDate(option.effective_from)} - {asDate(option.effective_through)}
            </Typography>
          </Box>
        </Box>
      )}
      renderInput={params => (
        <TextField
          {...params}
          label="Prior Auth Originator ID"
          variant="standard"
          fullWidth
          error={error}
          helperText={helperText}
        />
      )}
    />
  )
}

export default PriorAuthorizationSelect
